import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {QrCodeScannerDialog} from "@/features/user/qrCodeScannerDialog";
import {UserChangeConfigurationDialogHandler} from "@/features/user/userChangeConfigurationDialog";
import {TimeAccountDashboardQueryParams} from "@/shared/queryParams";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {AssignmentPanel, PlaceholderAssignmentPanel} from "@/shared/admin/assignmentPanel";
import {CarAssignmentPanel, GroupAssignmentPanel, TaskAssignmentPanel} from "@/shared/admin/basicSameTypeAssignmentPanel";
import {UpdateUserHourlyRateDialog} from "./updateUserHourlyRateDialog";
import {FormUtil} from "@/shared/forms/formUtil";
import {promiseNow} from "@/shared/utils/promise";
import {
	mountTerminalQrCodeCardIsland,
	type TerminalQrCodeCardIslandCommands,
} from "@/features/user/terminalQrCodeCardIsland";
import {ReactIsland} from "@/shared/reactIsland";
import {containsNonPrintableASCIIChars, encodeBase64} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {copyOfObject, debounce, serializeObjectToArray, toggleNavigationTabs} from "@/shared/utils/misc";
import {Notify} from "@/shared/ui/notify";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDate, formatDecimalNumber, formatDuration, parseDecimalNumber, parseISODate} from "@/shared/utils/datefunctions";
import {AssignedGroup, Car, ErrorResponse, HolidayCalendar, User, WorkScheduleModel} from "@/shared/timr";
import {InvitationStatus, MailDeliveryStatus, TimeAccountState} from "@/shared/timrTypes";
import Modal = BootstrapModal.Modal;

export type UpdateRecordings = {
	updateRecordings: boolean;
	fromDate?: string;
	onlyChangeable?: boolean;
};

export class UserDialogHandler {
	public static EVENT_SAVED_USER = "event_saved_user";
	public static EVENT_RESEND_INVITATION = "event_resend_invitation";
	public static EVENT_REMOVE_USER_FROM_SUPPRESSION_LIST = "event_remove_user_from_suppression_list";

	private static RUNNING_TIMER_WARNING_DEFAULT_HOUR = 18;

	private dom: JQuery;
	private modal: Modal;
	private loginEdited: boolean;
	private hasChanged: boolean;

	private originalTaskLeader: boolean | null;
	private originalUserHourlyRate: number | null;
	private originalUserHourlyRateInternal: number | null;

	private timeAccountInitializedGroup: JQuery;
	private timeAccountNotInitializedGroup: JQuery;

	private user: User;
	private workScheduleModels: WorkScheduleModel[] = [];
	private holidayCalendars: HolidayCalendar[] = [];
	private generalFieldObserver: FieldObserver<User>;
	private workingTimeFieldObserver: FieldObserver<User>;
	private rightsFieldObserver: FieldObserver<User>;
	private userSettingsFieldObserver: FieldObserver<User["userSettings"]>;

	private groupAssignmentPanel: AssignmentPanel<AssignedGroup>;
	private carAssignmentPanel: AssignmentPanel<Car>;
	private taskAssignmentPanel?: TaskAssignmentPanel;

	private updateUserHourlyRateDialog: UpdateUserHourlyRateDialog;
	private readonly terminalQrCodeCardIsland?: ReactIsland<object, TerminalQrCodeCardIslandCommands>;

	/**
	 * Flag to indicate that we should show the change configuration dialog on modal close.
	 */
	private showChangeConfigurationDialogFlag: boolean;
	/**
	 * Flag to indicate that we should navigate to the account initialization page on dialog close.
	 * A value of `null` indicates that we should not navigate,
	 * otherwise it contains the user ID which is passed as a query parameter on navigation.
	 */
	private navigateToInitTimeAccountUserIdFlag: number | null;

	private userChangeConfigurationDialogHandler: UserChangeConfigurationDialogHandler;

	private removeUserFromSuppressionList: boolean;

	constructor(userChangeConfigurationDialogHandler: UserChangeConfigurationDialogHandler) {
		this.dom = $("#userDialog");
		this.terminalQrCodeCardIsland = mountTerminalQrCodeCardIsland("terminalQrCodeCardIsland");
		this.userChangeConfigurationDialogHandler = userChangeConfigurationDialogHandler;
		this.modal = new Modal({
			title: "user.crud.header",
			message: this.dom,
			size: "modal-xl",
			buttons: [
				{
					text: "userChangeConfigurationDialog.btn",
					css: "btn-default btnChangeConfiguration",
					left: true,
					action: () => this.showChangeConfigurationDialog(),
					visible: false
				},
				{
					text: "admin.timeaccount.init",
					css: "btn-primary btnInitTimeAccount",
					left: true,
					action: () => this.navigateToInitAccountInitialization(),
					visible: false
				},
				{
					text: "dialog.save",
					action: (modal, button) => {
						this.saveUser(button);
					},
					css: "btn-primary save-button"
				},
				{
					text: "dialog.cancel"
				}
			],
			onBeforeOpen: () => this.updateWorkingTimeTabButtons(),
			onOpen: () => {
				this.resizeAssignmentPanels();
				this.updateWorkingTimeTabFields(this.user);
				this.terminalQrCodeCardIsland?.dispatchCommand("reloadToken", undefined);
				$("#userEmail").focus();
			},
			onClose: () => {
				FormUtil.removeAllFeedback($("#userForm"), true);
				this.groupAssignmentPanel.setEditMode(false);
				this.carAssignmentPanel.setEditMode(false);
				this.taskAssignmentPanel?.setEditMode(false);
				FormUtil.switchTabNavigationToFirst($("ul.dialog-nav-tabs"));

				if (this.showChangeConfigurationDialogFlag) {
					this.showChangeConfigurationDialogFlag = false;

					this.userChangeConfigurationDialogHandler.setUser(this.user);
					this.userChangeConfigurationDialogHandler.setWorkScheduleModels(this.workScheduleModels);
					this.userChangeConfigurationDialogHandler.setHolidayCalendars(this.holidayCalendars);
					this.userChangeConfigurationDialogHandler.open();
				}

				if (this.navigateToInitTimeAccountUserIdFlag) {
					const link = TimeAccountDashboardQueryParams.buildLink({
						userFilter: this.navigateToInitTimeAccountUserIdFlag,
					}, "timeaccounts/timeAccountDashboard.html", "initializeTimeAccount");

					this.navigateToInitTimeAccountUserIdFlag = null;

					window.location.href = link;
				}

				if (this.removeUserFromSuppressionList) {
					this.removeUserFromSuppressionList = false;
					$(this).triggerHandler(UserDialogHandler.EVENT_REMOVE_USER_FROM_SUPPRESSION_LIST, this.user);
				}
			}
		});

		this.generalFieldObserver = new FieldObserver();
		this.workingTimeFieldObserver = new FieldObserver();
		this.rightsFieldObserver = new FieldObserver();
		this.userSettingsFieldObserver = new FieldObserver();
		this.setupListener();

		if ($("#groupAssignmentPanel").length > 0) {
			this.groupAssignmentPanel = new GroupAssignmentPanel({
				panelElement: $("#groupAssignmentPanel"),
				assignedSourceProvider: () => {
					const userId = this.user.id;

					if (userId) {
						return api("admin/loadUserGroupAssignments.json")
							.get({
								userId: userId
							})
							.then(data => data.groups);
					} else {
						return promiseNow([]);
					}
				},
				selectSourceProvider: () => promiseNow([]),
				editMessage: "car.groups.add",
				onOpen: () => {
					// dummy approach for marking this component as "dirty"
					this.hasChanged = true;
				},
				saveMessage: "admin.assignment.edit.end"
			});
		} else {
			this.groupAssignmentPanel = new PlaceholderAssignmentPanel();
		}

		const carSource = api("admin/cars.json")
			.get({
				usable: true
			})
			.then(cars => cars.data);

		this.carAssignmentPanel = new CarAssignmentPanel({
			panelElement: $("#carAssignmentPanel"),
			assignedSourceProvider: () => {
				const userId = this.user.id;

				if (userId) {
					return api("admin/loadUserCarAssignments.json")
						.get({
							userId: userId
						})
						.then(data => data.cars);
				} else {
					return promiseNow([]);
				}
			},
			selectSourceProvider: () => carSource,
			editMessage: "user.cars.add",
			onOpen: () => {
				// dummy approach for marking this component as "dirty"
				this.hasChanged = true;
			},
			saveMessage: "admin.assignment.edit.end"
		});

		if (globals.projectTimeEnabled) {
			this.taskAssignmentPanel = new TaskAssignmentPanel({
				panelElement: $("#taskAssignmentPanel"),
				assignedSourceProvider: () => {
					return promiseNow([]);
				},
				selectSourceProvider: () => promiseNow([]),
				editMessage: "user.tasks.add",
				onOpen: () => {
					// dummy approach for marking this component as "dirty"
					this.hasChanged = true;
				},
				saveMessage: "admin.assignment.edit.end"
			}, () => this.user ? this.user.id : null);
		}

		$("ul.nav.nav-tabs").children().first().addClass("active");
		$("#userForm > div.tab-content").children().first().addClass("active in");

		$(window).resize(debounce(() => this.resizeAssignmentPanels(), this, 50));

		this.dom.find('a[data-toggle="tab"]').on("shown.bs.tab", (e) => {
			const tabId = $(e.target).attr("href");
			if (tabId === "#tabUserCarAssignments") {
				this.carAssignmentPanel.updateView();
			} else if (tabId === "#tabUserGroupAssignments") {
				this.groupAssignmentPanel.updateView();
			} else if (tabId === "#tabUserTaskAssignments") {
				this.taskAssignmentPanel?.updateView();
			}

			this.updateWorkingTimeTabButtons();
		});

		const onEditModeChanged = () => {
			const editMode = this.carAssignmentPanel.isInEditMode()
				|| this.groupAssignmentPanel.isInEditMode()
				|| this.taskAssignmentPanel?.isInEditMode();

			// Toggle all buttons of the dialog
			this.modal.getButtons("").toggle(!editMode);
			toggleNavigationTabs("#tabNavigation", editMode);
			// update working time tab buttons to ensure correct visibility
			this.updateWorkingTimeTabButtons();
		};

		this.carAssignmentPanel.onEditModeChanged(onEditModeChanged);
		this.groupAssignmentPanel.onEditModeChanged(onEditModeChanged);
		this.taskAssignmentPanel?.onEditModeChanged(onEditModeChanged);

		// preload the not-/initialized group via a variable to avoid problems at the first loading opening of a user dialog
		this.timeAccountInitializedGroup = $("#timeAccountInitializedGroup");
		this.timeAccountNotInitializedGroup = $("#timeAccountNotInitializedGroup");

		this.initTerminalTab();

		this.updateUserHourlyRateDialog = new UpdateUserHourlyRateDialog({container: $("#updateUserHourlyRateDialog")});
	}

	private setupListener() {
		this.setupGeneralListener();
		this.setupWorkingTimeTabListener();
		this.setupRightsTabListener();
		this.setupEmailTabListener();

		$("#userResendInvitationButton").click(() => {
			$(this).triggerHandler(UserDialogHandler.EVENT_RESEND_INVITATION, this.user);
		});

		$("#userOnSuppressionListButton").click(() => {
			// show a confirmation dialog first, if there are unsaved changes
			if (this.hasAnyInputChanged()) {
				const outerModalDialog = this.modal;
				const saveButton = outerModalDialog.getButtons("save-button");
				BootstrapModal.confirm({
					title: "user.changesPendingDialog.title",
					message: "user.changesPendingDialog.message",
					yesLabel: "user.changesPendingDialog.yes",
					noLabel: "user.changesPendingDialog.no",
					yesAction: (modal) => {
						// set a flag so that the onClose callback for TimrUserDialogHandler knows that the dialog should be shown
						this.removeUserFromSuppressionList = true;
						modal.close();
						saveButton.click();
					},
					noAction: (modal) => {
						// set a flag so that the onClose callback for TimrUserDialogHandler knows that the dialog should be shown
						this.removeUserFromSuppressionList = true;
						outerModalDialog.close();
						modal.close();
					}
				});
			} else {
				// set a flag so that the onClose callback for TimrUserDialogHandler knows that the dialog should be shown
				this.removeUserFromSuppressionList = true;
				this.modal.close();
			}
		});

		$("#buttonScanQrCode").click(() => QrCodeScannerDialog.show(scanResult =>
			this.externalTerminalTokenInput.val(scanResult)
		));
	}

	public setUser(user: User) {
		this.user = copyOfObject(user);
		// Reset dirty flag when loading a user
		this.hasChanged = false;
		this.generalFieldObserver.setModel(this.user);
		this.workingTimeFieldObserver.setModel(this.user);
		this.rightsFieldObserver.setModel(this.user);
		this.userSettingsFieldObserver.setModel(this.user.userSettings);

		this.originalUserHourlyRate = this.user.hourlyRate;
		this.originalUserHourlyRateInternal = this.user.hourlyRateInternal;
		this.originalTaskLeader = this.user.taskLeader;

		this.updateGeneralFields(this.user);

		this.updateWorkingTimeTabFields(this.user);
		$.when(this.loadHolidayCalendars(), this.loadWorkScheduleModels()).done(() => {
			this.updateWorkingTimeTabFields(this.user);
		});

		// Rights tab
		this.updateRightsTabFields(this.user);

		// Email settings
		this.toggleRunningTimerWarning(this.user.userSettings.runningTimerWarningAfterHour != null);
		this.updateEmailTabFields(this.user);

		this.setSaveButtonLabel(this.user.id == null
			? messages["user.button.invite"]
			: messages["dialog.save"]);

		this.loginEdited = this.user.login !== this.user.email;

		this.groupAssignmentPanel.reloadAssigned();
		this.carAssignmentPanel.reloadAssigned();
		this.taskAssignmentPanel?.reloadAssigned();

		this.updateTitle();
		this.toggleTerminalTab(this.user);
		this.updateTerminalQrCodeCard(this.user);
		// always clear external token input when setting the User (basically when Dialog is openend)
		this.externalTerminalTokenInput.val(null);
	}

	private get externalTerminalTokenInput() {
		return $("#externalTerminalAuthenticationKey");
	}

	private updateGeneralFields(user: User) {
		$("#userEmail").val(user.email);
		$("#userLogin").val(user.login);

		$("#userLastname").val(user.lastname);
		$("#userFirstname").val(user.firstname);

		const invitation = user.invitation;
		$("#userPendingInvitation").toggle(!!invitation && invitation.status === InvitationStatus.Open);
		$("#userOnSuppressionList").toggle(!!invitation && invitation.status !== InvitationStatus.Open && user.mailDeliveryStatus === MailDeliveryStatus.BOUNCED);

		$("#userExternalId").val(user.externalId);
		$("#userEmployeeNumber").val(user.employeeNumber);

		$("#userHourlyRate").val(formatDecimalNumber(user.hourlyRate));

		$("#userHourlyRateInternal").val(formatDecimalNumber(user.hourlyRateInternal));
	}

	private setupGeneralListener() {
		this.generalFieldObserver.observeInput("#userLastname", "lastname");
		this.generalFieldObserver.observeInput("#userFirstname", "firstname");

		this.generalFieldObserver.observeInput("#userExternalId", "externalId");
		this.generalFieldObserver.observeInput("#userEmployeeNumber", "employeeNumber");

		// allow empty field (null value) for hourlyRate and hourlyRateInternal
		this.generalFieldObserver.observeInput("#userHourlyRate", "hourlyRate", selector => parseDecimalNumber($(selector).val() as string, null));
		this.generalFieldObserver.observeInput("#userHourlyRateInternal", "hourlyRateInternal", selector => parseDecimalNumber($(selector).val() as string, null));

		this.generalFieldObserver.onFieldValueChange((event, data) => {
			this.updateGeneralFields(this.user);
		});

		const updateLogin = () => {
			this.user.email = $("#userEmail").val() as string;
			if (!this.loginEdited) {
				this.user.login = this.user.email;
				$("#userLogin").val(this.user.login);
			}
			this.hasChanged = true;
		};

		const loginChanged = () => {
			this.user.login = $("#userLogin").val() as string;
			this.loginEdited = ($("#userEmail").val() as string) !== this.user.login;
			this.hasChanged = true;

			this.updateTitle();
		};

		$("#userEmail").keyup(updateLogin).change(updateLogin);
		$("#userLogin").keyup(loginChanged).change(loginChanged);

		$("#userPasswordRadios").change(() => {
			const usePassword = this.updatePasswordFields();

			const saveLabel = usePassword
				? messages["dialog.save"]
				: messages["user.button.invite"];

			this.setSaveButtonLabel(saveLabel);
		});
	}

	private updatePasswordFields(): boolean {
		const usePassword = $("#setPasswordRadio").prop("checked") as boolean;
		$("#userPasswordFields").toggle(usePassword);
		return usePassword;
	}

	private updateTitle() {
		let message = messages[this.user.id == null ? "user.crud.header.create" : "user.crud.header.edit"];

		if (this.user.fullname) {
			message += ": " + this.user.fullname;
		}

		this.modal.setTitle(message, true);
	}

	private updateWorkingTimeTabFields(user: User) {
		const fieldsToDeactivateIfTimeAccountStateIsExit = $("[data-deactivate-if-exit]");
		Input.enable(fieldsToDeactivateIfTimeAccountStateIsExit);

		$("#timeAccountInitializedExitInfo").toggle(this.user.timeAccountState === TimeAccountState.EXIT);

		$("#userWorkHoursPerWeek").val(formatDecimalNumber(user.workHoursPerWeek));

		$("#userVacationDaysPerYear").val(formatDecimalNumber(user.vacationDaysPerYear));
		$("#userVacationYearStart").val(formatDate(parseISODate(user.vacationYearStart)));

		$("#userWorkScheduleModel").text(user.workScheduleModelName);

		$("#userHolidayCalendar").text(this.holidayCalendars.find(holidayCalendar => holidayCalendar.id === user.holidayCalendarId)?.description || "");

		$("#userVacationUnit").text(messages[`user.crud.vacationUnit.${user.vacationUnit}`]);

		$("#userOvertimeAllowanceAllIn").prop("checked", user.overtimeAllowanceAllIn);

		$("#userTrustBasedWorkingTime").text(user.trustBasedWorkingTime ? messages["text.active"] : messages["text.inactive"]);
		$("#userWorkingTimeTracking").text(user.workingTimeTracking ? messages["text.active"] : messages["text.inactive"]);

		$("#userVacationAccount").text(user.vacationAccount ? messages["text.active"] : messages["text.inactive"]);

		const overtimeDurationIncludedField = $("#userOvertimeDurationIncluded");
		const showOvertimeIncludedOptions = user.overtimeAllowanceAllIn || user.overtimeDurationIncluded > 0 && user.workingTimeTracking;

		if (!user.overtimeAllowanceAllIn && user.overtimeDurationIncluded !== undefined) {
			overtimeDurationIncludedField.text(
				user.overtimeDurationIncluded > 0 ? `${formatDuration(user.overtimeDurationIncluded)} ${messages["user.crud.overtimeDurationIncluded.unit"]}` : messages["user.crud.overtimeDurationIncluded.none"]
			);
		} else if (user.overtimeAllowanceAllIn) {
			overtimeDurationIncludedField.text(messages["user.crud.overtimeAllowanceAllIn"]);
		}

		$("#userAllowClearNegativeWorkDurationBalanceContainer").toggle(showOvertimeIncludedOptions);
		$("#userAllInSetWorkDurationBalanceZeroContainer").toggle(user.overtimeAllowanceAllIn && user.workingTimeTracking);
		$("#userAllowClearNegativeWorkDurationBalance").text(user.allowClearNegativeWorkDurationBalance ? messages["text.active"] : messages["text.inactive"]);
		$("#userAllInSetWorkDurationBalanceZero").text(user.allInSetWorkDurationBalanceZero ? messages["text.active"] : messages["text.inactive"]);

		const userOvertimeDeductionOrder = $(`#userOvertimeDeductionAccountOrderContainer`)
			.toggle(showOvertimeIncludedOptions)
			.empty();
		user.overtimeDeductionAccountOrder.forEach((account, index) => {
			userOvertimeDeductionOrder.append(`
					<div class="form-group workingTimeTracking-field mb-5">
						<label class="col-md-4 control-label text-right input-info-text pb-0">
							${index < 1 ? messages["user.crud.deductFromAccount.label"] : ""}
						</label>
						<div class="col-md-8 pt-8">
							${index + 1}. ${messages["user.crud.deductFromAccount.type." + account]}
						</div>
					</div>
				`);
		});

		$("#entryAndResignDates").show();
		$("#userEntryDate").val(formatDate(parseISODate(user.entryDate)));
		$("#userResignDate").val(formatDate(parseISODate(user.resignDate)));

		if (SecurityStatus.isTrustBasedWorkingTimeAllowed()) {
			this.toggleWorkingTimeTrackingFields(user);
			this.toggleVacationAccountFields(user);
		}

		if (this.user.timeAccountState === TimeAccountState.EXIT) {
			Input.disable(fieldsToDeactivateIfTimeAccountStateIsExit);
		}

		this.timeAccountInitializedGroup.toggle(this.user.timeAccountState !== TimeAccountState.NO_TIME_ACCOUNT);
		this.timeAccountNotInitializedGroup.toggle(this.user.timeAccountState === TimeAccountState.NO_TIME_ACCOUNT);
	}

	private toggleWorkingTimeTrackingFields(user: User) {
		$(".workingTimeTracking-field").toggle(user.workingTimeTracking);
	}

	private toggleVacationAccountFields(user: User) {
		$(".vacationAccount-field").toggle(user.vacationAccount);
	}

	private setupWorkingTimeTabListener() {
		this.workingTimeFieldObserver.observeInput("#userWorkHoursPerWeek", "workHoursPerWeek", FieldObserver.getDecimalNumberFromField);
		this.workingTimeFieldObserver.observeInput("#userVacationYearStart", "vacationYearStart", FieldObserver.getDateJsonFromField);
		this.workingTimeFieldObserver.observeInput("#userVacationDaysPerYear", "vacationDaysPerYear", FieldObserver.getDecimalNumberFromField);
		this.workingTimeFieldObserver.observeInput("#userEntryDate", "entryDate", FieldObserver.getDateJsonFromField);
		this.workingTimeFieldObserver.observeInput("#userResignDate", "resignDate", FieldObserver.getDateJsonFromField);

		this.workingTimeFieldObserver.onFieldValueChange((() => this.updateWorkingTimeTabFields(this.user)));
	}

	private updateRightsTabFields(user: User) {
		if (this.user.overallTaskLeader === true) {
			this.user.taskLeader = true;
		}
		// the account owner is always administrator, so the setting cannot be changed for the owner (TIMR-12860)
		const isAccountOwner = user.id != null && user.id === globals.ownerId;
		$("#userAdmin").prop("checked", user.admin).prop("disabled", isAccountOwner);
		$("#userAdminOwnerInfo").toggle(isAccountOwner);
		$("#userTaskManager").prop("checked", user.taskManager).prop("disabled", user.admin);
		$("#userOverallTaskLeader").prop("checked", user.overallTaskLeader);

		$("#userTaskLeader")
			.prop("checked", user.taskLeader)
			.prop("disabled", this.user.overallTaskLeader === true);

		// Empty string if null or undefined and "true"/"false" if true/false
		$("#userworkingTimeDateTimeChangeableOverride").val(user.workingTimeDateTimeChangeableOverride == null ? "" : "" + user.workingTimeDateTimeChangeableOverride);
		$("#userprojectTimeDateTimeChangeableOverride").val(user.projectTimeDateTimeChangeableOverride == null ? "" : "" + user.projectTimeDateTimeChangeableOverride);
		$("#userDriveLogDateTimeChangeableOverride").val(user.driveLogDateTimeChangeableOverride == null ? "" : "" + user.driveLogDateTimeChangeableOverride);
		$("[name='workingTimeRecordingMobileAllowedOverride']").val(user.workingTimeRecordingMobileAllowedOverride === null ? "" : "" + user.workingTimeRecordingMobileAllowedOverride);
		$("[name='projectTimeRecordingMobileAllowedOverride']").val(user.projectTimeRecordingMobileAllowedOverride === null ? "" : "" + user.projectTimeRecordingMobileAllowedOverride);
		$("[name='driveLogRecordingMobileAllowedOverride']").val(user.driveLogRecordingMobileAllowedOverride === null ? "" : "" + user.driveLogRecordingMobileAllowedOverride);

		// show checked dummy checkbox with info when the current user is owner and edits his own user
		const ownerEditsHimself = globals.userId === user.id && globals.isOwner;
		$("#userSubscriptionManagerOwnerDummy").toggle(ownerEditsHimself);
		$("#userSubscriptionManagerLabel").toggle(!ownerEditsHimself);

		// update dummy checkbox when the current user is non owner (the owner is always marked as subscription manager)
		if (!globals.isOwner) {
			$("#userSubscriptionManagerNonOwnerDummyCheckbox").prop("checked", user.subscriptionManager || user.id === globals.ownerId);
		}

		$("input[name=subscriptionManager]").prop("checked", user.subscriptionManager);
	}

	private setupRightsTabListener() {
		this.rightsFieldObserver.observeInput("#userAdmin", "admin");
		this.rightsFieldObserver.observeInput("#userTaskManager", "taskManager");
		this.rightsFieldObserver.observeInput("#userTaskLeader", "taskLeader");
		this.rightsFieldObserver.observeInput("#userOverallTaskLeader", "overallTaskLeader");
		this.rightsFieldObserver.observeInput("#userSubscriptionManager", "subscriptionManager");

		const overrideSelectAsBool = (sel: JQuery.Selector) => {
			const val = $(sel).val();
			if (val === "true") {
				return true;
			} else if (val === "false") {
				return false;
			} else {
				return null;
			}
		};

		this.rightsFieldObserver.observeInput("#userworkingTimeDateTimeChangeableOverride", "workingTimeDateTimeChangeableOverride", overrideSelectAsBool);
		this.rightsFieldObserver.observeInput("#userprojectTimeDateTimeChangeableOverride", "projectTimeDateTimeChangeableOverride", overrideSelectAsBool);
		this.rightsFieldObserver.observeInput("#userDriveLogDateTimeChangeableOverride", "driveLogDateTimeChangeableOverride", overrideSelectAsBool);
		this.rightsFieldObserver.observeInput("#userWorkingTimeRecordingMobileAllowedOverride", "workingTimeRecordingMobileAllowedOverride", overrideSelectAsBool);
		this.rightsFieldObserver.observeInput("#userProjectTimeRecordingMobileAllowedOverride", "projectTimeRecordingMobileAllowedOverride", overrideSelectAsBool);
		this.rightsFieldObserver.observeInput("#userDriveLogRecordingMobileAllowedOverride", "driveLogRecordingMobileAllowedOverride", overrideSelectAsBool);

		this.rightsFieldObserver.onFieldValueChange(((event, data) => {
			if (data.field === "admin" && data.newValue) {
				this.user.taskManager = true;
			}
			this.updateRightsTabFields(this.user);
		}));
	}

	private updateEmailTabFields(user: User) {
		const userSettings = user.userSettings;

		$("#userNewsletter").prop("checked", !userSettings.unsubscribed);
		$("#runningTimerWarningAfterHour").val(userSettings.runningTimerWarningAfterHour != null ? userSettings.runningTimerWarningAfterHour : UserDialogHandler.RUNNING_TIMER_WARNING_DEFAULT_HOUR);
		$("#userWorkingTimeRequestStatusEmail").prop("checked", userSettings.workingTimeRequestStatusEmail);
	}

	private setupEmailTabListener() {
		this.userSettingsFieldObserver.observeInput("#userNewsletter", "unsubscribed", sel => !$(sel).is(":checked"));

		this.userSettingsFieldObserver.observeInput("#runningTimerWarningAfterHour", "runningTimerWarningAfterHour", sel => Number($(sel).val()));

		this.userSettingsFieldObserver.observeInput("#userWorkingTimeRequestStatusEmail", "workingTimeRequestStatusEmail", sel => $(sel).is(":checked"));

		this.userSettingsFieldObserver.onFieldValueChange(((event, data) => {
			this.updateEmailTabFields(this.user);
		}));

		$("#runningTimerWarning").change(() => {
			this.toggleRunningTimerWarning($("#runningTimerWarning").prop("checked") as boolean);
		});
	}

	private toggleRunningTimerWarning(show: boolean) {
		if (show && !this.user.userSettings.runningTimerWarningAfterHour) {
			this.user.userSettings.runningTimerWarningAfterHour = UserDialogHandler.RUNNING_TIMER_WARNING_DEFAULT_HOUR;
		}
		$("#runningTimerWarning").prop("checked", show);

		$(".toggleRunningTimerWarning").toggle(show);
	}

	public resetUser() {
		this.setUser({
			id: null,
			version: 0,
			fullname: "",
			lastname: "",
			firstname: "",
			login: "",
			disabled: false,
			taskManager: true,
			taskLeader: false,
			overallTaskLeader: false,
			email: "",
			admin: false,
			externalId: "",
			// @ts-expect-error TODO assignment of empty string to number properties is not correct
			hourlyRate: "",
			// @ts-expect-error TODO assignment of empty string to number properties is not correct
			hourlyRateInternal: "",
			// @ts-expect-error TODO assignment of empty string to number properties is not correct
			vacationDaysPerYear: "",
			userSettings: {
				workingTimeRequestStatusEmail: true,
				unsubscribed: true,
				runningTimerWarningAfterHour: UserDialogHandler.RUNNING_TIMER_WARNING_DEFAULT_HOUR
			},
			trustBasedWorkingTime: false,
			workingTimeTracking: true,
			vacationAccount: true,
			startOfFirstAlternatingWeek: null
		});
	}

	public open() {
		this.updatePasswordFields();
		this.modal.show();
	}


	private setSaveButtonLabel(label: string) {
		this.modal.getButtons("save-button").text(label);
	}

	private loadWorkScheduleModels(): JQueryPromise<void> {
		const loadingIndicator = $("#timeAccountNotInitializedGroupProcessingPanel");
		loadingIndicator.show();
		const workScheduleModelElement = $("#userWorkScheduleModel");
		if (workScheduleModelElement.length > 0) {
			api("admin/findWorkScheduleModelsForSelect.json")
				.get()
				.done((promise: WorkScheduleModel[]) => {
					this.workScheduleModels = promise;
					setTimeout(() => {
						loadingIndicator.hide();
					}, 0);
				});
		} else {
			return $.Deferred<void>().resolve().promise();
		}
	}

	private loadHolidayCalendars(): JQueryPromise<void> {
		const deferred = $.Deferred<void>();
		if (SecurityStatus.isHolidayCalendarAllowed()) {
			api("admin/holidayCalendars.json")
				.get()
				.then(data => {
					this.holidayCalendars = data.holidayCalendars;
					deferred.resolve();
				})
				.fail(() => {
					this.holidayCalendars = [];
					deferred.reject();
				});
			return deferred.promise();
		} else {
			this.holidayCalendars = [];
			return deferred.resolve().promise();
		}
	}

	private saveUser(button: JQuery) {
		const performSaveUser = (updateRecordings: UpdateRecordings) => {
			Button.setLoading(button);

			const userData = serializeObjectToArray({
				...this.user,
				newsletter: !this.user.userSettings.unsubscribed,
				runningTimerWarningAfterHour: $("#runningTimerWarning").prop("checked")
					? this.user.userSettings.runningTimerWarningAfterHour
					: null,
				workingTimeRequestStatusEmail: this.user.userSettings.workingTimeRequestStatusEmail,
				updateRecordings: updateRecordings.updateRecordings,
				updateHourlyRateFromDate: updateRecordings.fromDate ? updateRecordings.fromDate : null,
				onlyUpdateHourlyRateForChangeableRecords: updateRecordings.onlyChangeable ? updateRecordings.onlyChangeable : false
			});

			const groups = this.groupAssignmentPanel.getAssigned()
				.map(assigned => ({
					name: "assignedGroups",
					value: assigned.id + ""
				}));

			userData.push(...groups);

			const cars = this.carAssignmentPanel.getAssigned()
				.map(assigned => ({
					name: "assignedCars",
					value: assigned.id + ""
				}));

			userData.push(...cars);

			if (this.taskAssignmentPanel) {
				const assignedTasks = this.taskAssignmentPanel.getAssigned()
					.map(assigned => ({
						name: "assignedTasks",
						value: assigned.id + ""
					}));

				userData.push(...assignedTasks);

				const unassignedTasks = this.taskAssignmentPanel.getUnassigned()
					.map(assigned => ({
						name: "unassignedTasks",
						value: assigned.id + ""
					}));

				userData.push(...unassignedTasks);
			}

			api("admin/saveUser.json")
				.post(userData)
				.done((data) => {
					SecurityStatus.update(data.securityStatus);
					this.modal.close();
					$(this).triggerHandler(UserDialogHandler.EVENT_SAVED_USER, data.user);
					return data;
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors($("#userForm"), response.responseJSON.fieldErrors, true);
					}
				})
				.always(() => {
					Button.resetLoading(button);
				});
		};


		if (this.user.id != null) {
			this.checkTaskLeaderAssignments()
				.then(() => this.checkIfChangedHourlyRateShouldUpdateRecordings())
				.done(performSaveUser);
		} else {
			performSaveUser({updateRecordings: false});
		}
	}

	private navigateToInitAccountInitialization() {
		this.checkForChangesRunActionAndCloseDialog(() => {
			// set a flag so that the onClose callback for TimrUserDialogHandler knows that we should navigate to the account initialization page
			this.navigateToInitTimeAccountUserIdFlag = this.user.id;
		})
	}

	private showChangeConfigurationDialog() {
		this.checkForChangesRunActionAndCloseDialog(() => {
			// set a flag so that the onClose callback for TimrUserDialogHandler knows that the dialog should be shown
			this.showChangeConfigurationDialogFlag = true;
		})
	}

	private checkForChangesRunActionAndCloseDialog(action: () => void) {
		// show a confirmation dialog first, if there are unsaved changes
		if (this.hasAnyInputChanged()) {
			const outerModalDialog = this.modal;
			const saveButton = outerModalDialog.getButtons("save-button");
			BootstrapModal.confirm({
				title: "user.changesPendingDialog.title",
				message: "user.changesPendingDialog.message",
				yesLabel: "user.changesPendingDialog.yes",
				noLabel: "user.changesPendingDialog.no",
				yesAction: (modal) => {
					action();

					modal.close();
					saveButton.click();
				},
				noAction: (modal) => {
					action()

					outerModalDialog.close();
					modal.close();
				}
			});
		} else {
			action()
			this.modal.close();
		}
	}

	private checkTaskLeaderAssignments(): JQueryPromise<unknown> {
		const deferred = $.Deferred();

		const taskLeaderRevoked = this.originalTaskLeader && !this.user.taskLeader;

		if (SecurityStatus.isCorporateFeaturesAllowed() && taskLeaderRevoked) {
			api("admin/checkTaskLeaderAssignmentsForUser.json")
				.get({
					id: this.user.id
				}).done(data => {
				if (data.assignments > 0) {
					BootstrapModal.confirm({
						title: "dialog.remove.taskLeaderAssignments.title",
						message: $.validator.format(messages["dialog.remove.taskLeaderAssignments.message"], data.assignments.toString()),
						yesLabel: "dialog.remove.taskLeaderAssignments.yes",
						noLabel: "dialog.remove.taskLeaderAssignments.no",
						yesAction: (modal) => {
							deferred.resolve();
							modal.close();
						}
					});
				} else {
					deferred.resolve();
				}
			}).fail(() => void deferred.reject());
		} else {
			deferred.resolve();
		}

		return deferred.promise();
	}

	private hasAnyInputChanged(): boolean {
		return this.hasChanged ||
			this.generalFieldObserver.hasChanged() ||
			this.workingTimeFieldObserver.hasChanged() ||
			this.rightsFieldObserver.hasChanged() ||
			this.userSettingsFieldObserver.hasChanged()
	}

	private checkIfChangedHourlyRateShouldUpdateRecordings(): JQueryPromise<UpdateRecordings> {
		const deferred: JQueryDeferred<UpdateRecordings> = $.Deferred();

		const hourlyRateChanged = this.user.hourlyRate !== this.originalUserHourlyRate;
		const hourlyRateInternalChanged = this.user.hourlyRateInternal !== this.originalUserHourlyRateInternal;

		if (SecurityStatus.isBudgetsAllowed() && (hourlyRateChanged || hourlyRateInternalChanged)) {
			api("admin/checkIfProjectTimeExistsForUser.json")
				.get({
					id: this.user.id
				})
				.done((data) => {
					if (data.foundRecordings > 0) {
						this.updateUserHourlyRateDialog.open(result => {
							deferred.resolve(result);
						});
					} else {
						deferred.resolve({updateRecordings: false});
					}
				})
				.fail(() => void deferred.reject());
		} else {
			deferred.resolve({updateRecordings: false});
		}

		return deferred.promise();
	}

	private initTerminalTab() {
		$("#addExternalTerminalAuthenticationKey").click(() => {
			const externalTerminalTokenInput = this.externalTerminalTokenInput
			const externalTerminalTokenInputValue: string = externalTerminalTokenInput.val() as string;

			// Show error message in case the input contains non-printable ASCII chars
			if (containsNonPrintableASCIIChars(externalTerminalTokenInputValue)) {
				Input.removeFeedback(externalTerminalTokenInput)
				Input.addFeedback(externalTerminalTokenInput, 'has-error', messages["admin.user.terminal.externalQrCode.invalidChars"]);
				return;
			}
			api("admin/setExternalTerminalAuthenticationKeyForUser.json")
				.post({
					userId: this.user.id,
					// Base64 encode the token
					externalTerminalAuthenticationKey: encodeBase64(externalTerminalTokenInputValue)
				})
				.then((response: { user: User }) => {
					Notify.success(messages["admin.user.terminal.externalQrCode.addSuccess"]);
					$(this).triggerHandler(UserDialogHandler.EVENT_SAVED_USER, response.user);
					this.setUser(response.user);
				})
				.fail((response: ErrorResponse) => {
					FormUtil.markFieldError(
						this.externalTerminalTokenInput,
						response.responseJSON?.fieldErrors
					);
				})
				.always(() => this.externalTerminalTokenInput.val(null));
		});

		$("#resetExternalTerminalAuthenticationKey").click(() => {
			BootstrapModal.confirm({
				title: "admin.user.terminal.externalQrCode.delete.title",
				message: "admin.user.terminal.externalQrCode.delete.message",
				yesLabel: "confirmation.delete",
				noLabel: "confirmation.cancel",
				yesAction: (modal: BootstrapModal.Modal) => {
					api("admin/resetExternalTerminalAuthenticationKeyForUser.json")
						.post({
							userId: this.user.id
						})
						.then((response: { user: User }) => {
							this.externalTerminalTokenInput.val(null);
							Notify.success(messages["admin.user.terminal.externalQrCode.deleteSuccess"]);
							$(this).triggerHandler(UserDialogHandler.EVENT_SAVED_USER, response.user);
							this.setUser(response.user);
						});

					modal.close();
				}
			});
		});
	}

	private updateTerminalQrCodeCard(user: User) {
		this.terminalQrCodeCardIsland?.dispatchCommand("showUser", user?.id != null
			? {id: user.id, name: user.fullname, login: user.login}
			: null);
	}

	private toggleTerminalTab(user: User) {
		const tabUserTerminalAuthTokensContainer = $("#tabUserTerminalAuthTokensContainer");

		if (user) {
			const terminalTab = $('a[href="#tabUserTerminalAuthTokens"]').closest("li");
			tabUserTerminalAuthTokensContainer.show();
			terminalTab.show();
		} else {
			tabUserTerminalAuthTokensContainer.hide();
		}

		const externalQRCodeEmpty = $("#externalQRCodeEmpty");
		const externalQRCodeDiv = $("#externalQRCodeDiv");

		if (user?.externalTerminalAuthenticationKeySet) {
			externalQRCodeEmpty?.hide();
			externalQRCodeDiv?.show();
		} else {
			externalQRCodeEmpty?.show();
			externalQRCodeDiv?.hide();
		}
	}

	private resizeAssignmentPanels() {
		const screenHeight = $(window).height();

		// Estimated height of other dialog components + padding + margin ...
		const panelHeight = Math.max(screenHeight - 450, 240);
		this.groupAssignmentPanel.setScrollHeight(panelHeight);
		this.carAssignmentPanel.setScrollHeight(panelHeight);
		this.taskAssignmentPanel?.setScrollHeight(panelHeight);
	}

	private updateWorkingTimeTabButtons(): void {
		// show change configuration button only on working time tab and time account is allowed

		const timeAccountInitialized = this.user.timeAccountState !== TimeAccountState.NO_TIME_ACCOUNT;
		const tabActive = $("#tabUserWorkingTime.active").length > 0

		this.modal.getButtons("btnChangeConfiguration")
			.prop("disabled", !timeAccountInitialized)
			.toggle(timeAccountInitialized && tabActive && SecurityStatus.isTimeAccountAllowed());
		this.modal.getButtons("btnInitTimeAccount")
			.prop("disabled", timeAccountInitialized)
			.toggle(!timeAccountInitialized && tabActive && SecurityStatus.isTimeAccountAllowed() && !this.user.disabled);
	}
}
